// Sends every <form data-contact-form> to the shared POST /api/contact
// (App.registerContactForm: Brevo + MailLimiter + honeypot).
//
// The form markup and all its texts come from the page's LD; this module
// only reads them from data attributes:
//   data-subject     mail subject (the site address is appended when given);
//                    a "subject" field typed by the visitor takes its place,
//   data-url-prefix  label put before the site address in the message,
//   data-sending / data-success / data-error  status texts,
//   data-network-error  optional text for a failed request (network, timeout,
//                    HTTP error); without it data-error covers that case too.
// An element with data-contact-line="<prefix>" (e.g. a fieldset of radios)
// adds the line "<prefix> <value>" of its first named control to the message,
// after the site address; disabled or empty controls add nothing.
// Fields: name, email, message, optional phone, subject and url, and the
// off-screen honeypot input named "contact_extra" (an unusual name, so
// browsers do not autofill it).
// The status element ([data-contact-status]) gets data-state="info|success|error".

import { CONTACT_HONEYPOT_FIELD } from '../../contact/ContactDto.js';

type StatusState = 'info' | 'success' | 'error';

/** Per-site behaviour on top of the default (native validation, status line). */
export interface ContactFormOptions {
    /** Checks by field name (on the trimmed value). An invalid field gets the
     * "error" class and its [data-error-for] message is shown: on submit,
     * on blur and 1 s after typing stops (the last two only when not empty). */
    validators?: Record<string, (value: string) => boolean>;
    /** Replaces < and > in every sent value. */
    escapeHtml?: boolean;
    /** "Sending" goes into the button label ([data-contact-label]) after a
     * spinner, instead of into the status line. */
    sendingInButton?: boolean;
    /** The status line gets class "success|error" and display:block instead
     * of data-state, and is hidden again this many ms after the request. */
    hideStatusAfterMs?: number;
    /** Aborts the request after this many ms. */
    timeoutMs?: number;
}

const text = (data: FormData, name: string): string => String(data.get(name) ?? '').trim();

/** A form field sent as a "<prefix> <value>" line before the visitor's message. */
export interface ContactLineField {
    name: string;
    prefix: string;
}

export function buildContactPayload(
    data: FormData,
    subject: string,
    urlPrefix: string,
    lineFields: ContactLineField[] = [],
): Record<string, string> {
    const url = text(data, 'url');
    const lines = [
        ...(url ? [`${urlPrefix} ${url}`] : []),
        ...lineFields.filter(({ name }) => text(data, name)).map(({ name, prefix }) => `${prefix} ${text(data, name)}`),
    ];
    const message = [lines.join('\n'), text(data, 'message')].filter(Boolean).join('\n\n');
    const phone = text(data, 'phone');
    const typedSubject = text(data, 'subject');
    const mailSubject = typedSubject || subject;
    return {
        name: text(data, 'name'),
        email: text(data, 'email'),
        ...(data.has('phone') ? { phone } : {}),
        subject: url ? `${mailSubject}: ${url}` : mailSubject,
        message,
        [CONTACT_HONEYPOT_FIELD]: String(data.get('contact_extra') ?? ''),
    };
}

const escapeHtml = (value: string) => value.replace(/</g, '&lt;').replace(/>/g, '&gt;');

function bindValidation(form: HTMLFormElement, validators: Record<string, (value: string) => boolean>) {
    const fields = Object.keys(validators).map(
        (name) => form.elements.namedItem(name) as HTMLInputElement | HTMLTextAreaElement,
    );
    const errorFor = (field: HTMLInputElement | HTMLTextAreaElement) =>
        form.querySelector<HTMLElement>(`[data-error-for="${field.name}"]`)!;
    const show = (field: HTMLInputElement | HTMLTextAreaElement, visible: boolean) => {
        errorFor(field).style.display = visible ? 'block' : 'none';
        field.classList.toggle('error', visible);
    };
    const isValid = (field: HTMLInputElement | HTMLTextAreaElement) => validators[field.name](field.value.trim());
    // While typing or leaving a field an empty value is not reported yet.
    const check = (field: HTMLInputElement | HTMLTextAreaElement) =>
        show(field, !isValid(field) && field.value.trim() !== '');

    for (const field of fields) {
        let timeout: ReturnType<typeof setTimeout>;
        field.addEventListener('blur', () => check(field));
        field.addEventListener('input', () => {
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                if (field.value.trim().length > 0) check(field);
            }, 1000);
        });
    }

    return {
        clear: () => fields.forEach((field) => show(field, false)),
        /** Marks every invalid field, empty ones included. */
        validateAll: () => fields.map((field) => (show(field, !isValid(field)), isValid(field))).every(Boolean),
    };
}

function bindContactForm(form: HTMLFormElement, options: ContactFormOptions): void {
    const status = form.querySelector<HTMLElement>('[data-contact-status]')!;
    const button = form.querySelector<HTMLButtonElement>('button[type="submit"]')!;
    const label = form.querySelector<HTMLElement>('[data-contact-label]');
    const submitLabel = label?.textContent ?? '';
    const validation = options.validators ? bindValidation(form, options.validators) : undefined;
    const lineFields = [...form.querySelectorAll<HTMLElement>('[data-contact-line]')].map((element) => ({
        name: element.querySelector('[name]')!.getAttribute('name')!,
        prefix: element.dataset.contactLine!,
    }));
    const show = (message: string, state: StatusState) => {
        status.textContent = message;
        if (options.hideStatusAfterMs) {
            status.className = state;
            status.style.display = 'block';
        } else {
            status.hidden = false;
            status.dataset.state = state;
        }
    };

    form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (validation) {
            validation.clear();
            if (!validation.validateAll()) return;
        }
        const { subject, urlPrefix, sending, success, error, networkError } = form.dataset;
        const payload = buildContactPayload(new FormData(form), subject!, urlPrefix!, lineFields);
        if (options.escapeHtml) {
            for (const key of Object.keys(payload)) payload[key] = escapeHtml(payload[key]);
        }

        button.disabled = true;
        if (options.sendingInButton) label!.innerHTML = `<span class="loading"></span> ${escapeHtml(sending!)}`;
        else show(sending!, 'info');
        try {
            const response = await fetch(form.action, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                ...(options.timeoutMs ? { signal: AbortSignal.timeout(options.timeoutMs) } : {}),
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const isJson = response.headers.get('content-type')?.includes('application/json');
            const result = isJson ? await response.json() : { success: true };
            if (result.success === false) {
                show(error!, 'error');
            } else {
                form.reset();
                validation?.clear();
                show(success!, 'success');
            }
        } catch {
            show(networkError ?? error!, 'error');
        } finally {
            button.disabled = false;
            if (options.sendingInButton) label!.textContent = submitLabel;
            if (options.hideStatusAfterMs) {
                setTimeout(() => (status.style.display = 'none'), options.hideStatusAfterMs);
            }
        }
    });
}

export function bindContactForms(root: ParentNode = document, options: ContactFormOptions = {}): void {
    root.querySelectorAll<HTMLFormElement>('form[data-contact-form]').forEach((form) => bindContactForm(form, options));
}
